export type LocalizedSelectionValue = Record<string, string>;
export type SelectionValue = string | LocalizedSelectionValue;

export const isLocalizedSelectionValue = (value: unknown): value is LocalizedSelectionValue => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

export const normalizeLocale = (locale?: string): string => {
  return (locale || 'en').split('-')[0].toLowerCase();
};

export const getLocalizedSelectionLabel = (
  value: SelectionValue | null | undefined,
  locale: string,
): string => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return value;
  }

  const normalizedLocale = normalizeLocale(locale);

  return (
    value[normalizedLocale] ||
    value.en ||
    value.de ||
    Object.values(value).find((entry) => typeof entry === 'string' && entry.trim() !== '') ||
    ''
  );
};

export const serializeSelectionValue = (value: SelectionValue | null | undefined): string => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
};

export const parseSelectionValue = (value: unknown): SelectionValue | null => {
  if (value === null || value === undefined || value === '') return null;

  if (isLocalizedSelectionValue(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  const trimmed = value.trim();

  if (!trimmed) return null;

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (isLocalizedSelectionValue(parsed)) {
        return parsed;
      }
    } catch {
      return value;
    }
  }

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const first = parsed[0];
        if (isLocalizedSelectionValue(first)) return first;
        if (typeof first === 'string') return first;
      }
    } catch {
      return value;
    }
  }

  return value;
};

export const selectionValuesEqual = (a: unknown, b: unknown): boolean => {
  const parsedA = parseSelectionValue(a);
  const parsedB = parseSelectionValue(b);

  if (parsedA === null && parsedB === null) return true;
  if (parsedA === null || parsedB === null) return false;

  if (typeof parsedA === 'string' && typeof parsedB === 'string') {
    return parsedA === parsedB;
  }

  return serializeSelectionValue(parsedA) === serializeSelectionValue(parsedB);
};

export const getSelectionOptions = (values: Array<SelectionValue> | undefined): SelectionValue[] => {
  if (!Array.isArray(values)) return [];

  return values
    .map(parseSelectionValue)
    .filter((value): value is SelectionValue => value !== null);
};

export const getSelectionValueForSelect = (value: unknown, options: SelectionValue[]): string => {
  const parsedValue = parseSelectionValue(value);

  if (!parsedValue) return '';

  const matchingOption = options.find((option) => selectionValuesEqual(option, parsedValue));

  return matchingOption ? serializeSelectionValue(matchingOption) : '';
};

export const getDefaultSelectionValue = (
  defaultValue: unknown,
  options: SelectionValue[],
): SelectionValue | null => {
  const parsedDefault = parseSelectionValue(defaultValue);

  if (parsedDefault) {
    const matchingOption = options.find((option) => selectionValuesEqual(option, parsedDefault));
    return matchingOption ?? parsedDefault;
  }

  return options[0] ?? null;
};

export const coerceSelectionValueForStorage = (value: unknown): string => {
  const parsed = parseSelectionValue(value);
  return serializeSelectionValue(parsed);
};

export const getSelectionDisplayText = (value: unknown, locale: string): string => {
  const parsed = parseSelectionValue(value);
  return parsed ? getLocalizedSelectionLabel(parsed, locale) : '';
};